<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DisplayCanvasOptions from "./DisplayCanvasOptions.vue";
import DisplayToolsOptions from "./DisplayToolsOptions.vue";
import { isDisplayDialogOpen } from "@/stores/DisplayDialogState";
import { canvasTopPx } from "@/stores/CanvasLayoutState";
import { useDisplaySettings } from "@/composable/useDisplaySettings";
import { CANVAS_UI_SCALES } from "@/stores/Settings";
import { toolbarWidth } from "@/utils/toolbar-layout";

/**
 * Modale Affichage (maquette V5) : réglages d'affichage à gauche, outils
 * affichés dans la barre à droite. Sans voile et posée à droite de la barre
 * d'outils : chaque changement se voit aussitôt (aperçu en direct).
 */
const { resetDisplay } = useDisplaySettings();

const showResetConfirm = ref(false);

function confirmReset() {
  resetDisplay();
  showResetConfirm.value = false;
}

const EDGE_GAP = 16;
/** En deçà, la modale prend toute la largeur (par-dessus la barre). */
const NARROW_SCREEN = 640;

/**
 * Position figée à l'ouverture (px écran : la modale n'est pas zoomée), à
 * droite de la barre la plus large possible (3 colonnes, plus grande taille
 * d'interface) : masquer des outils ou changer la taille ne la déplace pas
 * sous le doigt.
 */
const WIDEST_TOOLBAR =
  toolbarWidth(3) * Math.max(...Object.values(CANVAS_UI_SCALES));

const left = ref(0);
const top = ref(0);

function placeDialog() {
  left.value =
    window.innerWidth < NARROW_SCREEN ? 8 : WIDEST_TOOLBAR + EDGE_GAP;
  top.value = canvasTopPx.value + 8;
}

watch(
  isDisplayDialogOpen,
  (open) => {
    if (open) placeDialog();
  },
  { immediate: true },
);

// L'état est partagé : quitter le plan modale ouverte ne doit pas la rouvrir
// toute seule au plan suivant.
onBeforeUnmount(() => {
  isDisplayDialogOpen.value = false;
});

const dialogStyle = computed(() => ({
  "--display-dialog-left": `${left.value}px`,
  "--display-dialog-top": `${top.value}px`,
}));
const dialogWidth = computed(
  () => `min(920px, calc(100vw - ${left.value + 8}px))`,
);
</script>

<template>
  <el-dialog
    v-model="isDisplayDialogOpen"
    class="canvas-display-dialog"
    modal-class="canvas-display-overlay"
    :style="dialogStyle"
    :width="dialogWidth"
    :show-close="false"
    append-to-body
  >
    <template #header="{ titleId }">
      <div class="display-dialog-header">
        <PhosphorIcon name="sliders-horizontal" :size="18" />
        <span :id="titleId" class="display-dialog-title">
          {{ $t("canvas:display.title") }}
        </span>
        <DRBtnIcon
          type="button"
          variant="flat-white"
          :size="30"
          phosphor-icon="x"
          :aria-label="$t('close')"
          @click="isDisplayDialogOpen = false"
        />
      </div>
    </template>

    <div class="display-dialog-columns">
      <DisplayCanvasOptions />
      <DisplayToolsOptions />
    </div>

    <template #footer>
      <div class="d-flex align-items-center justify-content-between gap-2">
        <DRBtn
          variant="outline-white"
          size="sm"
          phosphor-icon="arrow-counter-clockwise"
          :label="$t('canvas:display.reset')"
          @click="showResetConfirm = true"
        />
        <DRBtn
          variant="primary"
          size="sm"
          :label="$t('canvas:display.done')"
          @click="isDisplayDialogOpen = false"
        />
      </div>
    </template>

    <ConfirmationModal
      v-model="showResetConfirm"
      :title="$t('canvas:display.resetConfirm.title')"
      :message="$t('canvas:display.resetConfirm.message')"
      :confirm-label="$t('canvas:display.resetConfirm.confirm')"
      @confirm="confirmReset"
    />
  </el-dialog>
</template>

<style lang="scss" scoped>
.display-dialog-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.display-dialog-title {
  flex-grow: 1;
  font-size: 16px;
  font-weight: 600;
}

// Deux colonnes, une seule quand la place manque.
.display-dialog-columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(270px, 100%), 1fr));
  // Large écart sur grand écran, sans perdre les 2 colonnes en portrait.
  column-gap: clamp(22px, 4vw, 48px);
}
</style>

<!-- Non scopé : el-dialog est téléporté dans <body> -->
<style lang="scss">
// Pas de voile : la barre d'outils et le plan restent visibles derrière.
.el-overlay.canvas-display-overlay {
  background-color: transparent;
}

.el-dialog.canvas-display-dialog {
  position: absolute;
  top: var(--display-dialog-top);
  left: var(--display-dialog-left);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - var(--display-dialog-top) - 16px);
  // Barre d'adresse des navigateurs mobiles (hors PWA) déduite.
  max-height: calc(100dvh - var(--display-dialog-top) - 16px);
  margin: 0;
  padding: 14px 18px;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);

  .el-dialog__header {
    padding: 0;
  }

  .el-dialog__body {
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow-y: auto;
  }

  .el-dialog__footer {
    margin-top: 6px;
    padding: 10px 0 0;
    border-top: 1px solid var(--canvas-border);
  }

  // Contrôles Element Plus aux couleurs de l'outil dessin.
  .display-switch {
    --el-switch-on-color: var(--canvas-btn-active-bg);
    --el-switch-off-color: rgba(255, 255, 255, 0.18);
    flex-shrink: 0;
  }

  .display-segmented {
    --el-segmented-bg-color: rgba(255, 255, 255, 0.06);
    --el-segmented-color: rgba(255, 255, 255, 0.8);
    --el-segmented-item-hover-color: #fff;
    --el-segmented-item-hover-bg-color: rgba(255, 255, 255, 0.08);
    --el-segmented-item-selected-bg-color: var(--canvas-btn-active-bg);
    --el-segmented-item-selected-color: #1a1a1a;
    flex-shrink: 0;
  }

  .display-checkbox {
    --el-checkbox-text-color: rgba(255, 255, 255, 0.85);
    --el-checkbox-bg-color: transparent;
    --el-checkbox-input-border: 1.5px solid rgba(255, 255, 255, 0.35);
    --el-checkbox-checked-text-color: rgba(255, 255, 255, 0.85);
    --el-checkbox-checked-bg-color: var(--canvas-btn-active-bg);
    --el-checkbox-checked-input-border-color: var(--canvas-btn-active-bg);
    --el-checkbox-input-border-color-hover: var(--canvas-btn-active-bg);
    height: 26px;
    margin-right: 0;
    font-size: 12px;
  }
}
</style>
